import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { storage } from '../../services/firebase';
import styles from './attachmentform.module.css';
import { useAuth } from '../../contexts/authcontext';

function AttachmentForm({ onAttachmentAdded, onCancel, existingAttachments, bidId }) {
  const { user } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [inputType, setInputType] = useState('url'); // 'url' or 'file'
  
  console.log('AttachmentForm: Initializing with existingAttachments:', existingAttachments);
  
  // Initialize form with default values
  const { register, handleSubmit, reset, formState: { errors }, setValue, watch } = useForm({
    defaultValues: {
      attachmentUrl: existingAttachments?.url || '',
      attachmentFile: null
    }
  });
  
  // Monitor the URL value for debugging
  const currentUrl = watch("attachmentUrl");
  useEffect(() => {
    console.log("Current URL value in form:", currentUrl);
  }, [currentUrl]);

  // Update form values when existingAttachments changes
  useEffect(() => {
    if (existingAttachments) {
      setValue('attachmentUrl', existingAttachments.url || '', { shouldValidate: true });
      if (existingAttachments.fileUrl) {
        setInputType('file');
      } else if (existingAttachments.url) {
        setInputType('url');
      }
      reset({
        attachmentUrl: existingAttachments.url || '',
        attachmentFile: null
      }, { keepDefaultValues: false });
    } else {
      reset({
        attachmentUrl: '',
        attachmentFile: null
      }, { keepDefaultValues: false });
    }
  }, [existingAttachments, setValue, reset]);

  // Add a ref to directly manipulate the URL input if needed
  const urlInputRef = useRef(null);
  
  // Additional effect to ensure the URL field is properly filled
  useEffect(() => {
    // Direct DOM manipulation as a fallback to ensure the URL field shows the value
    if (inputType === 'url' && urlInputRef.current && existingAttachments?.url) {
      urlInputRef.current.value = existingAttachments.url;
      console.log('AttachmentForm: Manually set URL input value via ref:', existingAttachments.url);
    }
  }, [existingAttachments, inputType]);

  // When switching tabs, just change the tab, do not reset or setValue
  const handleTabSwitch = (type) => {
    setInputType(type);
  };

  const onFormSubmit = async (data) => {
    console.log('AttachmentForm: Form submission started with data:', 
      { 
        url: data.attachmentUrl, 
        fileExists: !!data.attachmentFile && !!data.attachmentFile[0],
        fileName: data.attachmentFile && data.attachmentFile[0] ? data.attachmentFile[0].name : 'No file' 
      }
    );
    
    console.log('Current user:', user);
    if (!user || !user.uid) {
      console.error('User is not authenticated');
      setUploadError('You must be logged in to upload files');
      return;
    }
    console.log('User authenticated with UID:', user.uid);
    console.log('BidId for upload:', bidId);
    
    setIsUploading(true);
    setUploadError(null);
    
    try {
      // Start with a copy of the existing attachments to preserve fields
      const attachmentData = { ...(existingAttachments || {}) };
      let fileDeleted = false;

      if (inputType === 'url') {
        let url = data.attachmentUrl ? data.attachmentUrl.trim() : '';
        if (url === '') {
          // Explicitly clear the URL
          attachmentData.url = '';
        } else if (url) {
          if (!url.match(/^https?:\/\//)) {
            url = `https://${url}`;
          }
          attachmentData.url = url;
        }
        // If switching to URL, do not touch fileUrl or storagePath
      } else {
        // Handle file upload to Firebase Storage
        if (!data.attachmentFile || !data.attachmentFile[0]) {
          if (!existingAttachments?.fileUrl) {
            throw new Error('No file selected');
          }
        } else {
          // If there's an existing file in storage, delete it first
          if (existingAttachments?.fileUrl && existingAttachments?.storagePath) {
            try {
              const oldFileRef = ref(storage, existingAttachments.storagePath);
              await deleteObject(oldFileRef);
              fileDeleted = true;
            } catch (deleteError) {
              console.error('AttachmentForm: Error deleting old file:', deleteError);
            }
          }
          // Upload new file
          const file = data.attachmentFile[0];
          
          // Check file size (5MB limit)
          const maxSize = 5 * 1024 * 1024; // 5MB in bytes
          if (file.size > maxSize) {
            throw new Error(`File size (${(file.size / (1024 * 1024)).toFixed(1)}MB) exceeds the 5MB limit. Please choose a smaller file.`);
          }
          
          const fileId = uuidv4();
          const safeBidId = bidId || `no-bid-id-${fileId}`;
          const filePath = `bids/${safeBidId}/${fileId}_${file.name}`;
          const fileRef = ref(storage, filePath);
          try {
            await uploadBytes(fileRef, file);
            const downloadUrl = await getDownloadURL(fileRef);
            attachmentData.fileUrl = downloadUrl;
            attachmentData.fileName = file.name;
            attachmentData.storagePath = filePath;
          } catch (uploadError) {
            throw new Error(`File upload failed: ${uploadError.message}`);
          }
        }
      }
      // Add timestamp
      attachmentData.createdAt = new Date().toISOString();
      // Only allow submission if at least one of fileUrl or url is present (or if explicitly cleared)
      const isValidSubmission =
        (inputType === 'url' && ('url' in attachmentData)) ||
        (inputType === 'file' && (attachmentData.fileUrl || existingAttachments?.fileUrl));
      if (isValidSubmission) {
        await onAttachmentAdded(attachmentData);
        reset();
      } else {
        setUploadError('Please provide a valid URL or upload a file.');
      }
    } catch (error) {
      setUploadError(error.message || 'An unknown error occurred');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = async () => {
    if (!existingAttachments?.fileUrl) return;
    
    setIsUploading(true);
    setUploadError(null);
    
    try {
      // Delete file from storage
      if (existingAttachments.storagePath) {
        try {
          const fileRef = ref(storage, existingAttachments.storagePath);
          await deleteObject(fileRef);
          console.log('AttachmentForm: Successfully deleted file from storage:', existingAttachments.storagePath);
        } catch (deleteError) {
          console.error('AttachmentForm: Error deleting file from storage:', deleteError);
          throw new Error('Failed to delete file from storage');
        }
      }
      
      // Keep URL but remove file data
      const updatedAttachment = {
        ...existingAttachments
      };
      
      // Remove file-related fields
      delete updatedAttachment.fileUrl;
      delete updatedAttachment.fileName;
      delete updatedAttachment.storagePath;
      
      // If there's no URL either, remove the entire attachment
      if (!existingAttachments.url) {
        await onAttachmentAdded(null);
      } else {
        await onAttachmentAdded(updatedAttachment);
      }
      
      reset();
    } catch (error) {
      setUploadError(error.message || 'Failed to remove file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveUrl = async () => {
    if (!existingAttachments?.url) return;
    
    setIsUploading(true);
    setUploadError(null);
    
    try {
      // Keep file but remove URL
      const updatedAttachment = {
        ...existingAttachments
      };
      
      // Remove URL field
      delete updatedAttachment.url;
      
      // If there's no file either, remove the entire attachment
      if (!existingAttachments.fileUrl) {
        await onAttachmentAdded(null);
      } else {
        await onAttachmentAdded(updatedAttachment);
      }
      
      reset();
    } catch (error) {
      setUploadError(error.message || 'Failed to remove URL');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={styles.attachmentFormContainer}>
      <div className={styles.inputTypeToggle}>
        <button 
          type="button" 
          className={`${styles.inputTypeButton} ${inputType === 'url' ? styles.active : ''}`}
          onClick={() => handleTabSwitch('url')}
        >
          URL
        </button>
        <button 
          type="button" 
          className={`${styles.inputTypeButton} ${inputType === 'file' ? styles.active : ''}`}
          onClick={() => handleTabSwitch('file')}
        >
          File
        </button>
      </div>
      
      {uploadError && (
        <div className={styles.error}>{uploadError}</div>
      )}
      
      <form onSubmit={handleSubmit(onFormSubmit)} className={styles.form}>
        {inputType === 'url' ? (
          <div className={styles.formGroup}>
            <label>URL</label>
            <input 
              type="text" 
              ref={urlInputRef}
              defaultValue={existingAttachments?.url || ''}
              {...register('attachmentUrl', { 
                required: false,
                pattern: {
                  value: /^(https?:\/\/|\\\\|file:\/\/|[a-zA-Z]:|www\.)[\w\-~:%./=&?#@!$'()*+,;[\]]+$|^$/,
                  message: 'Enter a valid URL or leave blank'
                }
              })}
              placeholder="e.g., www.example.com/document" 
            />
            {errors.attachmentUrl && <span className={styles.error}>{errors.attachmentUrl.message}</span>}
            {existingAttachments?.url && inputType === 'url' && (
              <div className={styles.existingUrl}>
                <span>Current URL: {existingAttachments.url}</span>
                <button
                  type="button"
                  className={styles.removeUrlButton}
                  onClick={handleRemoveUrl}
                  disabled={isUploading}
                >
                  Remove URL
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className={styles.formGroup}>
            <label>File</label>
            <input 
              type="file" 
              {...register('attachmentFile', { 
                required: inputType === 'file' && !existingAttachments?.fileUrl ? 'File is required' : false
              })}
            />
            {errors.attachmentFile && <span className={styles.error}>{errors.attachmentFile.message}</span>}
            {existingAttachments?.fileUrl && inputType === 'file' && (
              <div className={styles.existingFile}>
                <div>
                  <span>Current file: {existingAttachments.fileName || 'Attachment'}</span>
                  <small>(Upload a new file to replace)</small>
                </div>
                <button
                  type="button"
                  className={styles.removeFileButton}
                  onClick={handleRemoveFile}
                  disabled={isUploading}
                >
                  Remove File
                </button>
              </div>
            )}
          </div>
        )}
        
        <div className={styles.formActions}>
          <button 
            type="submit" 
            className={styles.addButton}
            disabled={isUploading}
          >
            {isUploading ? 'Adding...' : 'Update'}
          </button>
          

          
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onCancel}
            disabled={isUploading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default AttachmentForm; 